<template>
  <main>
    <b-overlay :show="loading">
      <validation-observer ref="form">
        <b-alert show variant="primary" class="mb-2">
          <strong>Cara penempatan aset:</strong>
          (1) Pilih no. pengadaan →
          (2) Klik baris aset di kiri →
          (3) Isi lokasi →
          (4) Klik <em>Tambah ke antrian</em> (bisa berulang) →
          (5) Klik <em>Simpan penempatan</em>.
        </b-alert>

        <b-card class="mb-2">
          <b-row>
            <b-col sm="12" md="6">
              <b-form-group v-if="!isInvoiceAvailable" label="Pilih No. Pengadaan">
                <v-select
                  v-model="selectedInvoice"
                  :options="pengadaans"
                  label="text"
                  placeholder="Cari / pilih pengadaan..."
                />
              </b-form-group>
              <div v-else>
                <small class="text-muted d-block">Pengadaan</small>
                <strong>ID {{ pengadaan.id }} — No. Pengadaan: {{ pengadaan.no }}</strong>
              </div>
            </b-col>
            <b-col sm="12" md="6" class="d-flex align-items-end justify-content-md-end">
              <b-button
                class="mt-2"
                variant="outline-primary"
                @click.prevent="$router.push(`/asset-penyimpanan/add-empty`)"
              >
                <span class="align-middle">
                  <feather-icon icon="PlusIcon" /> Tambah Penyimpanan Kosong
                </span>
              </b-button>
            </b-col>
          </b-row>
        </b-card>

        <b-row>
          <!-- List aset dari pengadaan -->
          <b-col sm="12" md="6">
            <b-card>
              <h5 class="mb-1">Aset dari pengadaan</h5>
              <p class="text-muted small mb-2">
                Klik baris untuk memilih aset yang akan ditempatkan.
              </p>

              <div
                v-if="!hasPengadaanSelected"
                class="text-center text-muted py-4"
              >
                Pilih no. pengadaan terlebih dahulu.
              </div>
              <div
                v-else-if="!assets.length"
                class="text-center text-muted py-4"
              >
                Tidak ada aset pada pengadaan ini.
              </div>
              <div v-else class="barang-pick-list">
                <div
                  v-for="item in assets"
                  :key="item.id"
                  class="barang-pick-item"
                  :class="{ 'is-selected': isBarangSelected(item) }"
                  role="button"
                  tabindex="0"
                  @click="selectBarang(item)"
                  @keydown.enter.prevent="selectBarang(item)"
                >
                  <div class="d-flex align-items-start justify-content-between">
                    <div class="pr-1">
                      <div class="barang-pick-name">
                        {{ item.asset ? item.asset.nama : "-" }}
                      </div>
                      <div class="text-muted small mb-50">
                        {{
                          item.asset && item.asset.satuan
                            ? item.asset.satuan.satuan
                            : "-"
                        }}
                      </div>
                      <div
                        class="barang-pick-sisa"
                        :class="{ 'is-empty': assetSisa(item) <= 0 }"
                      >
                        Sisa: {{ assetSisa(item) }}
                        {{
                          item.asset && item.asset.satuan
                            ? item.asset.satuan.satuan
                            : ""
                        }}
                      </div>
                    </div>
                    <feather-icon
                      v-if="isBarangSelected(item)"
                      icon="CheckCircleIcon"
                      size="22"
                      class="barang-pick-check flex-shrink-0"
                    />
                  </div>
                </div>
              </div>
            </b-card>
          </b-col>

          <!-- Form penempatan -->
          <b-col sm="12" md="6">
            <b-card>
              <h5 class="mb-1">Form penempatan</h5>
              <p class="text-muted small mb-2">
                <template v-if="selectedBarang && selectedBarang.asset">
                  Aset dipilih:
                  <strong>{{ selectedBarang.asset.nama }}</strong>
                  — sisa
                  <strong>{{ assetSisa(selectedBarang) }}</strong>
                  {{
                    selectedBarang.asset.satuan
                      ? selectedBarang.asset.satuan.satuan
                      : ""
                  }}
                </template>
                <template v-else>
                  Pilih aset di kiri sebelum mengisi lokasi.
                </template>
              </p>

              <fieldset :disabled="!selectedBarang">
                <b-form-group v-if="myGudang">
                  <label for="">Pilih Gudang</label>
                  <b-form-input disabled :value="myGudang.nama_gudang" />
                </b-form-group>

                <b-form-group>
                  <validation-provider
                    #default="{ errors }"
                    name="blok"
                    rules="required"
                  >
                    <label for="">Pilih Lokasi Aset <span class="text-danger">*</span></label>
                    <div class="d-flex align-items-start">
                      <v-select
                        class="flex-grow-1"
                        :options="bloks"
                        v-model="form.blok_id"
                        :required="!form.blok_id"
                        :reduce="(option) => option.value"
                        label="text"
                        placeholder="Pilih lokasi"
                      />
                      <b-button
                        class="ml-1 flex-shrink-0"
                        variant="outline-primary"
                        title="Tambah lokasi aset"
                        @click.prevent="openLokasiModal"
                      >
                        <feather-icon icon="PlusIcon" />
                      </b-button>
                    </div>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>

                <b-form-group label="Jumlah" label-for="nilai">
                  <b-form-input
                    id="nilai"
                    v-model="form.jumlah"
                    type="number"
                    disabled
                    placeholder="jumlah"
                  />
                </b-form-group>

                <b-form-group class="mb-0">
                  <b-button variant="primary" @click.prevent="addBarang">
                    Tambah ke antrian
                  </b-button>
                </b-form-group>
              </fieldset>
            </b-card>
          </b-col>

          <!-- Antrian penempatan -->
          <b-col cols="12" class="mt-2">
            <b-card>
              <div class="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <h5 class="mb-0">Antrian penempatan</h5>
                  <small class="text-muted">
                    {{ addedItems.length }} item siap disimpan
                  </small>
                </div>
                <b-button
                  variant="primary"
                  :disabled="!addedItems.length"
                  @click.prevent="submit"
                >
                  Simpan penempatan
                </b-button>
              </div>

              <div
                v-if="!addedItems.length"
                class="text-center text-muted py-4 border rounded"
              >
                Belum ada aset di antrian. Isi form lalu klik
                <strong>Tambah ke antrian</strong>.
              </div>
              <b-table
                v-else
                :fields="fieldsAddedItems"
                bordered
                striped
                responsive
                :items="addedItems"
              >
                <template #cell(no)="row">
                  {{ row.index + 1 }}
                </template>
                <template #cell(lokasi)="{ item }">
                  <strong>{{ item.lokasi }}</strong>
                </template>
                <template #cell(action)="row">
                  <b-link
                    class="text-danger"
                    title="Hapus dari antrian"
                    @click.prevent="addedItems.splice(row.index, 1)"
                  >
                    <feather-icon size="24" icon="XCircleIcon" />
                  </b-link>
                </template>
              </b-table>
            </b-card>
          </b-col>
        </b-row>

        <b-modal
          id="modal-tambah-lokasi-aset"
          v-model="showLokasiModal"
          title="Tambah Lokasi Aset"
          centered
          @ok.prevent="submitLokasi"
          ok-title="Simpan"
          cancel-title="Batal"
        >
          <b-form-group label="Gudang Aset">
            <b-form-input
              disabled
              :value="myGudang ? myGudang.nama_gudang : ''"
            />
          </b-form-group>
          <b-form-group label="Nama Lokasi / Blok">
            <b-form-input
              v-model="lokasiForm.blok"
              placeholder="Nama lokasi"
              @keyup.enter.prevent="submitLokasi"
            />
          </b-form-group>
        </b-modal>
      </validation-observer>
    </b-overlay>
  </main>
</template>

<script>
import {
  BTable,
  BOverlay,
  BLink,
  BFormGroup,
  BRow,
  BCol,
  BCard,
  BButton,
  BFormInput,
  BAlert,
  BModal,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { extend } from "vee-validate";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required } from "vee-validate/dist/rules";

extend("required", {
  ...required,
  message: "This field is required",
});
extend("positive", (value) => {
  return value > 0;
});

export default {
  components: {
    vSelect,
    BLink,
    ValidationObserver,
    ValidationProvider,
    BOverlay,
    BFormGroup,
    BTable,
    BRow,
    BCol,
    BCard,
    BButton,
    BFormInput,
    BAlert,
    BModal,
  },
  watch: {
    selectedBarang: {
      handler(val) {
        if (val && (val.sisa != null || val.jumlah != null)) {
          this.form.jumlah = parseInt(val.sisa != null ? val.sisa : val.jumlah, 10) || 0;
        }
      },
      deep: true,
    },
    async selectedInvoice(pengadaan) {
      if (pengadaan && pengadaan.data) {
        this.pengadaan = pengadaan.data;
        this.selectedBarang = null;
        this.loading = true;
        try {
          this.assets = await this.$store.dispatch("asset/getRincianPengadaan", {
            pengadaan_id: pengadaan.data.id,
          });
        } finally {
          this.loading = false;
        }
      }
    },
  },
  data: () => ({
    pengadaans: [],
    loading: false,
    selectedInvoice: null,
    selectedBarang: null,
    addedItems: [],
    fieldsAddedItems: [
      { key: "no", label: "No" },
      { key: "asset", label: "Nama Aset" },
      { key: "jumlah", label: "Jumlah" },
      { key: "lokasi", label: "Lokasi" },
      { key: "action", label: "#" },
    ],
    bloks: [],
    pengadaan: {
      id: null,
      no: null,
    },
    assets: [],
    form: {
      pengadaan_id: null,
      gudang_id: null,
      blok_id: null,
      jumlah: 0,
    },
    showLokasiModal: false,
    lokasiForm: {
      blok: "",
    },
    savingLokasi: false,
  }),
  computed: {
    isInvoiceAvailable() {
      return this.$route.query.pg;
    },
    hasPengadaanSelected() {
      return !!(this.isInvoiceAvailable || (this.selectedInvoice && this.selectedInvoice.data));
    },
  },
  methods: {
    assetSisa(item) {
      if (!item) return 0;
      const sisa = item.sisa != null ? item.sisa : item.jumlah;
      return parseInt(sisa, 10) || 0;
    },
    isBarangSelected(item) {
      return this.selectedBarang && this.selectedBarang.id === item.id;
    },
    selectBarang(item) {
      this.selectedBarang = item;
    },
    openLokasiModal() {
      this.lokasiForm.blok = "";
      this.showLokasiModal = true;
    },
    async submitLokasi() {
      const nama = (this.lokasiForm.blok || "").trim();
      if (!nama) {
        this.displayError({ message: "Nama lokasi tidak boleh kosong" });
        return;
      }
      if (!this.myGudang || !this.myGudang.id) {
        this.displayError({ message: "Gudang aset tidak ditemukan" });
        return;
      }

      this.savingLokasi = true;
      this.loading = true;
      try {
        const created = await this.$store.dispatch("asset/saveBlok", [
          {
            gudang_id: this.myGudang.id,
            blok: nama,
          },
        ]);
        await this.getBlok();
        if (created && created.id) {
          this.form.blok_id = created.id;
        } else {
          const found = this.bloks.find((b) => b.text === nama);
          if (found) this.form.blok_id = found.value;
        }
        this.showLokasiModal = false;
        this.lokasiForm.blok = "";
        this.displaySuccess({ message: "Lokasi aset berhasil ditambahkan" });
      } catch (e) {
        this.displayError(e);
      } finally {
        this.savingLokasi = false;
        this.loading = false;
      }
    },
    submit() {
      if (!this.addedItems.length) return;

      this.addedItems.map((item) => {
        delete item.asset;
        delete item.lokasi;
        item.pengadaan_id = item.p_id;
        item.gudang_id = this.myGudang.id;
      });
      const payload = [];
      Object.assign(payload, this.addedItems);

      this.loading = true;
      this.$store
        .dispatch("asset/savePenyimpanan", payload)
        .then(() => {
          this.loading = false;
          this.displaySuccess({
            message: "Aset berhasil ditempatkan!",
          });

          setTimeout(() => {
            this.$router.push("/asset-penyimpanan");
          }, 1000);
        })
        .catch((e) => {
          this.loading = false;
          this.displayError(e);
          return false;
        });
    },
    addBarang() {
      if (!this.selectedBarang) {
        this.$bvToast.toast("Pilih aset terlebih dahulu", {
          title: "Peringatan",
          variant: "danger",
          solid: true,
          toaster: "b-toaster-bottom-right",
        });
        return false;
      }
      if (this.form.jumlah > this.assetSisa(this.selectedBarang)) {
        this.$bvToast.toast(
          "Jumlah yang diisi tidak boleh lebih dari Sisa jumlah barang",
          {
            title: "Peringatan",
            variant: "danger",
            solid: true,
            toaster: "b-toaster-bottom-right",
          }
        );
        return false;
      }
      this.$refs.form
        .validate()
        .then((success) => {
          if (success) {
            const data = {};
            Object.assign(data, this.form);
            data.asset =
              this.selectedBarang && this.selectedBarang.asset
                ? this.selectedBarang.asset.nama
                : null;
            data.asset_id =
              this.selectedBarang && this.selectedBarang.asset
                ? this.selectedBarang.asset.id
                : null;

            const blok = this.bloks.find((b) => b.value == data.blok_id);
            data.lokasi = blok ? `Blok: ${blok.text}` : "";
            data.p_id = this.selectedBarang ? this.selectedBarang.id : null;
            this.addedItems.push(data);
          }
        })
        .catch((e) => console.error(e));
    },
    async getBlok() {
      this.bloks = [];
      if (!this.myGudang) return;
      const bloks = await this.$store.dispatch("asset/getBlok", {
        gudang_id: this.myGudang.id,
      });
      this.bloks = bloks.map((blok) => ({ value: blok.id, text: blok.blok }));
    },
    async getPengadaan() {
      const params = { order: "desc" };
      if (this.myGudang) params.gudang_id = this.myGudang.id;
      this.loading = true;
      let pengadaans = await this.$store.dispatch("asset/getPengadaan", params);
      pengadaans = pengadaans.filter(
        (item) => item.selesai == 0 && item.status != 2
      );
      this.pengadaans = pengadaans.map((item) => ({
        value: item.id,
        text: item.no,
        data: item,
      }));
      this.loading = false;
    },
  },
  async created() {
    this.getPengadaan();
    if (!this.myGudang) {
      this.$router.push("/asset-penyimpanan");
      return;
    }
    this.getBlok();

    if (this.isInvoiceAvailable) {
      this.loading = true;
      try {
        this.pengadaan = await this.$store.dispatch(
          "asset/getPengadaanById",
          this.$route.query.pg
        );
        this.assets = await this.$store.dispatch("asset/getRincianPengadaan", {
          pengadaan_id: this.$route.query.pg,
        });
      } finally {
        this.loading = false;
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.barang-pick-list {
  max-height: 480px;
  overflow-y: auto;
}

.barang-pick-item {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 0.5rem;
  padding: 0.75rem 0.9rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    background-color 0.15s ease;
  background: transparent;

  &:hover {
    border-color: rgba(40, 199, 111, 0.55);
    box-shadow: 0 0 0 1px rgba(40, 199, 111, 0.2);
  }

  &.is-selected {
    border-color: #28c76f;
    box-shadow: 0 0 0 2px rgba(40, 199, 111, 0.35),
      0 0 14px rgba(40, 199, 111, 0.25);
    background: rgba(40, 199, 111, 0.08);
  }
}

.barang-pick-name {
  font-weight: 600;
  line-height: 1.3;
  margin-bottom: 0.25rem;
}

.barang-pick-sisa {
  color: #059669;
  font-weight: 700;
  text-shadow: 0 0 10px rgba(16, 185, 129, 0.55);

  &.is-empty {
    color: #ea5455;
    text-shadow: 0 0 8px rgba(234, 84, 85, 0.35);
  }
}

.barang-pick-check {
  color: #28c76f;
  filter: drop-shadow(0 0 4px rgba(40, 199, 111, 0.65));
}

.dark-layout {
  .barang-pick-item {
    border-color: rgba(255, 255, 255, 0.18);

    &:hover {
      border-color: rgba(52, 211, 153, 0.65);
      box-shadow: 0 0 0 1px rgba(52, 211, 153, 0.25);
    }

    &.is-selected {
      border-color: #34d399;
      box-shadow: 0 0 0 2px rgba(52, 211, 153, 0.4),
        0 0 16px rgba(52, 211, 153, 0.35);
      background: rgba(52, 211, 153, 0.12);
    }
  }

  .barang-pick-sisa {
    color: #34d399;
    text-shadow: 0 0 12px rgba(52, 211, 153, 0.75);

    &.is-empty {
      color: #f87272;
      text-shadow: 0 0 10px rgba(248, 114, 114, 0.55);
    }
  }

  .barang-pick-check {
    color: #34d399;
  }
}
</style>
